import { drawWatermark } from "./draw";
import {
  findJpegExif,
  injectCopyrightFields,
  injectJpegComment,
  injectJpegExif,
  injectPngExif,
  injectPngText,
  readOrientation,
  stripPrivateTags,
} from "./exif";
import { mimeFor, outputFormatFor, type ProcessOptions, type Settings } from "./types";

export interface ProcessResult {
  blob: Blob;
  outName: string;
  width: number;
  height: number;
}

/** 输出文件名：`原名_watermarked.后缀` */
export function outputNameFor(name: string, outExt: string): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem}_watermarked${outExt}`;
}

/** 按 EXIF 方向计算摆正后的尺寸（5~8 需要交换宽高） */
export function orientedSize(w: number, h: number, orientation: number): [number, number] {
  return orientation >= 5 && orientation <= 8 ? [h, w] : [w, h];
}

function applyOrientation(
  ctx: OffscreenCanvasRenderingContext2D,
  orientation: number,
  w: number,
  h: number,
): void {
  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;
    case 3: ctx.transform(-1, 0, 0, -1, w, h); break;
    case 4: ctx.transform(1, 0, 0, -1, 0, h); break;
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
    case 6: ctx.transform(0, 1, -1, 0, h, 0); break;
    case 7: ctx.transform(0, -1, -1, 0, h, w); break;
    case 8: ctx.transform(0, -1, 1, 0, 0, w); break;
    default: break;
  }
}

/**
 * 单张图片的完整处理管线：
 * 解码（按 EXIF 方向摆正）→ 绘制水印 → 编码 → 写回清理后的 EXIF
 */
export async function processImage(
  name: string,
  buf: ArrayBuffer,
  st: Settings,
  opts: ProcessOptions,
  logo: ImageBitmap | null,
): Promise<ProcessResult> {
  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : "";

  const srcBytes = new Uint8Array(buf);
  const srcExif = ext === ".jpg" || ext === ".jpeg" || ext === ".jpe" ? findJpegExif(srcBytes) : null;
  const orientation = readOrientation(srcExif);

  // imageOrientation: "none" —— 明确不让浏览器自动旋转，方向由我们按 EXIF 统一处理
  const bitmap = await createImageBitmap(new Blob([buf], { type: mimeFor(ext) }), {
    imageOrientation: "none",
  });

  try {
    const [W, H] = orientedSize(bitmap.width, bitmap.height, orientation);
    const canvas = new OffscreenCanvas(W, H);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("无法创建画布上下文");

    if (orientation > 1) {
      ctx.save();
      applyOrientation(ctx, orientation, bitmap.width, bitmap.height);
      ctx.drawImage(bitmap, 0, 0);
      ctx.restore();
    } else {
      ctx.drawImage(bitmap, 0, 0);
    }

    drawWatermark(ctx, W, H, st, logo);

    const fmt = outputFormatFor(ext);
    const quality = fmt.mime === "image/png" ? undefined : opts.jpegQuality;
    let blob = await canvas.convertToBlob({ type: fmt.mime, quality });

    // 元数据：位置/方向/序列号等隐私信息强制抹除；版权署名写进文件本身
    const copyright = opts.copyright.trim();
    const isJpeg = fmt.mime === "image/jpeg";
    const isPng = fmt.mime === "image/png";

    if (srcExif || copyright) {
      let bytes: Uint8Array = new Uint8Array(await blob.arrayBuffer());

      if (srcExif) {
        const cleaned = copyright ? injectCopyrightFields(stripPrivateTags(srcExif), copyright) : stripPrivateTags(srcExif);
        if (isJpeg) bytes = injectJpegExif(bytes, cleaned);
        else if (isPng) bytes = injectPngExif(bytes, cleaned);
      }

      // 兜底通道：即使 EXIF 里塞不进，文件本身也带着版权声明
      if (copyright) {
        if (isJpeg) bytes = injectJpegComment(bytes, copyright);
        else if (isPng) {
          bytes = injectPngText(bytes, [
            { keyword: "Copyright", value: copyright },
            { keyword: "Author", value: copyright },
          ]);
        }
      }

      blob = new Blob([bytes as unknown as BlobPart], { type: fmt.mime });
    }

    return { blob, outName: outputNameFor(name, fmt.ext), width: W, height: H };
  } finally {
    bitmap.close();
  }
}
